import { useState, useEffect, useCallback, useMemo } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItem as MenuItemAction,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { getDevices } from "../../api/deviceAPI";
import {
  getNodeConfigurations,
  createNodeConfiguration,
  updateNodeConfiguration,
  deleteNodeConfiguration,
} from "../../api/nodeConfigurationAPI";
import {
  ConvertOIDValueToString,
  ConvertStringValueToOID,
} from "../../utils/configConverter";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { useObjectData, useEnumData } from "../../hooks/useLocalStorageData";
import { getCurrentTenant } from "../../config/tenantConfig";
import AlarmPhones from "./AlarmPhones";

export default function AdminConfigDataGrid() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  // Use custom hooks to get data from localStorage
  const objectData = useObjectData();
  const enumData = useEnumData();
  const isRcu = getCurrentTenant().projectCode === "RCU";
  const [allDevices, setAllDevices] = useState([]);
  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [devices, setDevices] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isUserOnlyRole, setIsUserOnlyRole] = useState(false);
  const [formData, setFormData] = useState({
    deviceType: "",
    value: "",
    object_idObject: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    configId: null,
  });

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleUpdate = () => {
    const configToEdit = allRows.find(
      (row) => row.idNodeConfiguration === selectedRowId
    );
    if (configToEdit) {
      setFormData({
        deviceType: configToEdit.deviceType, // Bu row'daki cihaz tipi kullanılacak
        value: configToEdit.nodeValue || configToEdit.defaultValue,
        object_idObject: configToEdit.object_idObject,
      });
      setEditingConfig(configToEdit);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAddConfigurationToNode = () => {
    const configToAdd = allRows.find(
      (row) =>
        row.idNodeConfiguration === selectedRowId ||
        `${row.object_idObject}-${row.deviceType}-${
          row.node_idNode || "default"
        }` === selectedRowId
    );
    if (configToAdd) {
      setFormData({
        deviceType: configToAdd.deviceType,
        value: configToAdd.defaultValue || "",
        object_idObject: configToAdd.object_idObject,
      });
      setEditingConfig(null);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteConfirmDialog({
      open: true,
      configId: selectedRowId,
    });
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteNodeConfiguration(deleteConfirmDialog.configId);

      setSnackbar({
        open: true,
        message:
          t("nodeComponents.configuration.deleteSuccess") ||
          "Configuration deleted successfully",
        severity: "success",
      });

      await refreshConfigurations();
    } catch (error) {
      console.error("Error deleting configuration:", error);
      setSnackbar({
        open: true,
        message:
          error.message ||
          t("nodeComponents.configuration.deleteError") ||
          "Failed to delete configuration",
        severity: "error",
      });
    } finally {
      setDeleteConfirmDialog({ open: false, configId: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ open: false, configId: null });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData({
      deviceType: "",
      value: "",
      object_idObject: "",
    });
  };

  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
      ...(field === "object_idObject" && { value: "" }),
    });
  };

  const handleSubmit = async () => {
    try {
      const adminId = localStorage.getItem("adminId");
      const selectedObject = objectData.find(
        (obj) => obj.idObject === parseInt(formData.object_idObject, 10)
      );

      let valueToPost = formData.value;
      if (selectedObject) {
        try {
          valueToPost = ConvertStringValueToOID(selectedObject, formData.value);
        } catch (error) {
          console.warn("Error converting value, using original:", error);
          valueToPost = formData.value;
        }
      }

      const configurationData = {
        idNodeConfiguration: editingConfig
          ? editingConfig.idNodeConfiguration
          : 0,
        deviceType: parseInt(formData.deviceType, 10),
        value: valueToPost,
        object_idObject: parseInt(formData.object_idObject, 10),
        node_idNode: node_id,
        admin_idAdmin: parseInt(adminId, 10),
      };

      if (editingConfig) {
        await updateNodeConfiguration(configurationData);
      } else {
        await createNodeConfiguration(configurationData);
      }

      setSnackbar({
        open: true,
        message:
          t(
            `nodeComponents.configuration.${
              editingConfig ? "updateSuccess" : "addSuccess"
            }`
          ) || "Configuration saved successfully",
        severity: "success",
      });

      handleDialogClose();

      await refreshConfigurations();
    } catch (error) {
      console.error("Error saving configuration:", error);
      setSnackbar({
        open: true,
        message:
          error.message ||
          t(
            `nodeComponents.configuration.${
              editingConfig ? "updateError" : "addError"
            }`
          ) ||
          "Failed to save configuration",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const columns = [
    {
      field: "deviceType",
      headerName: t("nodeComponents.configuration.deviceType") || "Device Type",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "objectName",
      headerName: t("nodeComponents.configuration.objectName") || "Object Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "defaultValue",
      headerName: t("nodeComponents.configuration.lettaValue") || "Letta Value",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "value",
      headerName:
        t("nodeComponents.configuration.defaultValue") || "Default Value",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { row } = params;
        const matchedObject = objectData.find(
          (obj) => obj.idObject === row.object_idObject
        );

        let isInvalid = false;

        if (
          matchedObject &&
          row.nodeValue !== null &&
          row.nodeValue !== undefined
        ) {
          if (matchedObject.type === 1) {
            if (matchedObject.enumList && matchedObject.enumList.length > 0) {
              const enumIdList = matchedObject.enumList
                .split(",")
                .map((id) => Number(id.trim()));
              const filteredEnumData = enumData.filter((enumItem) =>
                enumIdList.includes(enumItem.idEnum)
              );

              const currentLang = (localStorage.getItem("i18nextLng") || "tr").split("-")[0];
              const validValues = filteredEnumData.map((enumItem) => {
                if (currentLang === "tr") {
                  return enumItem.name1;
                } else if (currentLang === "de") {
                  return enumItem.name3;
                } else {
                  return enumItem.name2;
                }
              });

              isInvalid = !validValues.includes(row.nodeValue);
            }
          } else if (matchedObject.type === 0) {
            const booleanValues = ["true", "false", "1", "0", "True", "False"];
            isInvalid = !booleanValues.includes(String(row.nodeValue));
          } else if (
            matchedObject.type === 2 ||
            matchedObject.type === 10 ||
            matchedObject.type === 11 ||
            matchedObject.type === 12
          ) {
            isInvalid = isNaN(Number(row.nodeValue));
          } else if (matchedObject.type === 5) {
            isInvalid = isNaN(parseFloat(row.nodeValue));
          }
        }

        return (
          <div
            style={{
              backgroundColor: isInvalid
                ? theme.palette.mode === "dark"
                  ? "#4a1a1a"
                  : "#ff7f7f"
                : "transparent",
              color: isInvalid
                ? theme.palette.mode === "dark"
                  ? "#ffffff"
                  : "#000000"
                : "inherit",
              padding: "4px 8px",
              borderRadius: "4px",
              width: "100%",
              textAlign: isRcu ? "left" : "center",
            }}
          >
            {row.nodeValue}
          </div>
        );
      },
    },
    ...(!isUserOnlyRole
      ? [
          {
            field: "operations",
            headerName: t("nodeComponents.configuration.operations"),
            flex: 0.5,
            align: "center",
            headerAlign: "center",
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              const hasValue =
                params.row.value !== null && params.row.value !== undefined;
              const rowId =
                params.row.idNodeConfiguration ||
                `${params.row.object_idObject}-${params.row.deviceType}-${
                  params.row.node_idNode || "default"
                }`;

              return (
                <IconButton
                  onClick={(event) => handleMenuClick(event, rowId)}
                  size="small"
                >
                  {hasValue ? <MoreHorizIcon /> : <FileUploadIcon />}
                </IconButton>
              );
            },
          },
        ]
      : []),
  ];

  const refreshConfigurations = useCallback(async () => {
    try {
      if (node_id == null) {
        const result = await getNodeConfigurations();

        const configData = result || [];
        const filteredData = configData.filter(
          (item) => item.node_idNode === node_id || item.node_idNode === null
        );

        const processedData = filteredData.map((item) => {
          const matchedObject = objectData.find(
            (obj) => obj.idObject === item.object_idObject
          );

          const objectName = matchedObject
            ? matchedObject.name
            : `${t("nodeComponents.configuration.unknown") || "Unknown"} (${
                item.object_idObject
              })`;

          let convertedNodeValue = item.value;
          let convertedDefaultValue = item.defaultValue;

          if (matchedObject) {
            try {
              if (item.value !== null && item.value !== undefined) {
                convertedNodeValue = ConvertOIDValueToString(
                  matchedObject,
                  item.value
                );
              }
            } catch (error) {
              convertedNodeValue = item.value;
            }

            try {
              if (
                item.defaultValue !== null &&
                item.defaultValue !== undefined
              ) {
                convertedDefaultValue = ConvertOIDValueToString(
                  matchedObject,
                  item.defaultValue
                );
              }
            } catch (error) {
              convertedDefaultValue = item.defaultValue;
            }
          }

          return {
            ...item,
            objectName,
            nodeValue: convertedNodeValue,
            defaultValue: convertedDefaultValue,
          };
        });

        // Order rows by the backend display order: group first, then uiOrderId.
        const orderedData = (processedData || []).slice().sort(
          (a, b) =>
            (a.uiGroupId ?? 0) - (b.uiGroupId ?? 0) ||
            (a.uiOrderId ?? 0) - (b.uiOrderId ?? 0)
        );

        setAllRows(orderedData);
        setRows(orderedData);
      }
    } catch (error) {
      console.error("Error fetching node configurations:", error);
    }
  }, [node_id, t, objectData]);

  useEffect(() => {
    refreshConfigurations();
  }, [node_id, refreshConfigurations]);

  useEffect(() => {
    const checkUserRoles = () => {
      try {
        const userRoles = localStorage.getItem("userRoles");
        if (userRoles) {
          const roles = JSON.parse(userRoles);

          if (Array.isArray(roles)) {
            const hasOnlyUserRole =
              roles.length === 1 && roles.includes("user");
            setIsUserOnlyRole(hasOnlyUserRole);
          } else {
            setIsUserOnlyRole(false);
          }
        } else {
          setIsUserOnlyRole(false);
        }
      } catch (error) {
        setIsUserOnlyRole(false);
      }
    };

    checkUserRoles();
  }, []);

  useEffect(() => {
    async function fetchDevices() {
      try {
        const response = await getDevices();
        setAllDevices(response.data || response || []);
      } catch (error) {
        console.error(error);
      }
    }
    fetchDevices();
  }, [node_id]);

  useEffect(() => {
    if (allDevices && allDevices.length > 0) {
      if (node_id !== null) {
        const filtered = allDevices.filter(
          (device) => device.node_idNode === node_id
        );
        setDevices(filtered);
      } else {
        setDevices(allDevices);
      }
    }
  }, [allDevices, node_id]);

  useEffect(() => {
    setRows(allRows);
  }, [allRows]);

  // RCU: configurations are grouped into tabs by uiGroupId. The tab label is the
  // group's uiGroupName for the active language (tr=1, en=2, de=3, fr=4, es=5).
  const languageType = (() => {
    const lang = (i18n.language || "tr").toLowerCase();
    if (lang.startsWith("tr")) return 1;
    if (lang.startsWith("de")) return 3;
    if (lang.startsWith("fr")) return 4;
    if (lang.startsWith("es")) return 5;
    return 2;
  })();

  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const groups = useMemo(() => {
    if (!isRcu) return [];
    const map = new Map();
    rows.forEach((r) => {
      const gid = r.uiGroupId ?? 0;
      if (!map.has(gid)) {
        const label =
          r[`uiGroupName${languageType}`] ||
          r.uiGroupName2 ||
          r.uiGroupName1 ||
          `Group ${gid}`;
        map.set(gid, { id: gid, label });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  }, [rows, isRcu, languageType]);

  // Keep the selected tab valid as the available groups change.
  useEffect(() => {
    if (!isRcu) return;
    if (groups.length === 0) {
      if (selectedGroupId !== null) setSelectedGroupId(null);
      return;
    }
    if (!groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, isRcu, selectedGroupId]);

  const displayRows =
    isRcu && selectedGroupId !== null
      ? rows.filter((r) => (r.uiGroupId ?? 0) === selectedGroupId)
      : rows;

  // RCU: left-align every header + cell. A minWidth lets the grid scroll
  // horizontally on small screens instead of cramming the columns.
  const displayColumns = (
    isRcu
      ? columns.map((c) => ({ ...c, align: "left", headerAlign: "left" }))
      : columns
  ).map((c) => ({ minWidth: 120, ...c }));

  return (
    <>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" } }}>
        <Box
          sx={{
            mt: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        />

        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ opacity: 0, display: { xs: "none", md: "block" } }}
              >
                {t("nodeComponents.configuration.eventHistory")}
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ opacity: 0, display: { xs: "none", md: "block" } }}
              >
                {t("nodeComponents.configuration.eventHistory")}
              </Typography>
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ fontSize: { xs: "1.4rem", sm: "2rem", md: "3rem" } }}
              >
                {t("nodeComponents.configuration.adminTitle")}
              </Typography>
              <Box flex={1} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      {isRcu && <AlarmPhones />}
      {isRcu && groups.length > 0 && (
        <Box sx={{ maxWidth: { sm: "100%", md: "1700px" }, mb: 2 }}>
          <Tabs
            value={selectedGroupId ?? false}
            onChange={(e, v) => setSelectedGroupId(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 0,
              p: 0.5,
              borderRadius: 2,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.035)",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
            }}
          >
            {groups.map((g) => (
              <Tab
                key={g.id}
                label={g.label}
                value={g.id}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  minHeight: 36,
                  minWidth: "auto",
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: "text.primary",
                    bgcolor: (theme) => theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    color: (theme) => theme.palette.background.paper,
                    bgcolor: "text.primary",
                    boxShadow: 2,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <DataGrid
        columns={displayColumns}
        rows={displayRows}
        getRowId={(row) =>
          row.idNodeConfiguration
            ? row.idNodeConfiguration
            : `${row.object_idObject}-${row.deviceType}-${
                row.node_idNode || "default"
              }`
        }
        pagination
        initialState={{
          pagination: {
            paginationModel: { pageSize: 20 },
          },
        }}
        pageSizeOptions={[20]}
        disableSelectionOnClick
        sx={{
          height: { xs: 480, sm: 600 },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {(() => {
          const selectedRow = allRows.find(
            (row) =>
              row.idNodeConfiguration === selectedRowId ||
              `${row.object_idObject}-${row.deviceType}-${
                row.node_idNode || "default"
              }` === selectedRowId
          );

          const hasValue =
            selectedRow &&
            selectedRow.value !== null &&
            selectedRow.value !== undefined;

          if (hasValue) {
            return [
              <MenuItemAction onClick={handleUpdate} key="update">
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("nodeComponents.configuration.update")}
                />
              </MenuItemAction>,
              <MenuItemAction onClick={handleDelete} key="delete">
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("nodeComponents.configuration.delete")}
                />
              </MenuItemAction>,
            ];
          } else {
            return (
              <MenuItemAction onClick={handleAddConfigurationToNode}>
                <ListItemIcon>
                  <FileUploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    t("nodeComponents.configuration.addConfigurationToNode") ||
                    "Add Configuration to Node"
                  }
                />
              </MenuItemAction>
            );
          }
        })()}
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          {editingConfig
            ? t("nodeComponents.configuration.updateConfiguration") ||
              "Update Configuration"
            : t("nodeComponents.configuration.addConfiguration") ||
              "Add Configuration"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editingConfig && (
              <FormControl fullWidth>
                <InputLabel id="device-type-label">
                  {t("nodeComponents.configuration.deviceType") || "Device Type"}
                </InputLabel>
                <Select
                  labelId="device-type-label"
                  value={formData.deviceType}
                  label={
                    t("nodeComponents.configuration.deviceType") || "Device Type"
                  }
                  onChange={(e) => handleFormChange("deviceType", e.target.value)}
                >
                  {devices.map((device) => (
                    <MenuItem key={device.idDevice} value={device.type || 2}>
                      {device.nameDevice || device.serialDevice} ({t("nodeComponents.configuration.typeLabel") || "Type"}: {device.type || 2})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel id="object-label">
                {t("nodeComponents.configuration.object") || "Object"}
              </InputLabel>
              <Select
                labelId="object-label"
                value={formData.object_idObject}
                label={t("nodeComponents.configuration.object") || "Object"}
                onChange={(e) =>
                  handleFormChange("object_idObject", e.target.value)
                }
              >
                {(() => {
                  const configObjects = objectData.filter(
                    (obj) => obj.isConfig === 1
                  );
                  return configObjects.map((obj) => (
                    <MenuItem key={obj.idObject} value={obj.idObject}>
                      {obj.name} (
                      {t("nodeComponents.configuration.idLabel") || "ID"}:{" "}
                      {obj.idObject})
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>

            {(() => {
              const selectedObject = objectData.find(
                (obj) => obj.idObject === parseInt(formData.object_idObject, 10)
              );

              if (selectedObject && selectedObject.type === 1) {
                const currentLang = (localStorage.getItem("i18nextLng") || "tr").split("-")[0];

                let filteredEnumData = enumData;
                let enumIdList = [];
                if (
                  selectedObject.enumList &&
                  selectedObject.enumList.length > 0
                ) {
                  enumIdList = selectedObject.enumList
                    .split(",")
                    .map((id) => Number(id.trim()));
                  filteredEnumData = enumData.filter((enumItem) =>
                    enumIdList.includes(enumItem.idEnum)
                  );
                }

                return (
                  <FormControl fullWidth>
                    <InputLabel id="enum-value-label">
                      {t("nodeComponents.configuration.value") || "Value"}
                    </InputLabel>
                    <Select
                      labelId="enum-value-label"
                      value={formData.value}
                      label={t("nodeComponents.configuration.value") || "Value"}
                      onChange={(e) =>
                        handleFormChange("value", e.target.value)
                      }
                    >
                      {filteredEnumData.map((enumItem) => {
                        let displayName = "";
                        if (currentLang === "tr") {
                          displayName = enumItem.name1;
                        } else if (currentLang === "de") {
                          displayName = enumItem.name3;
                        } else {
                          displayName = enumItem.name2;
                        }
                        return (
                          <MenuItem key={enumItem.idEnum} value={displayName}>
                            {displayName} (
                            {t("nodeComponents.configuration.idLabel") || "ID"}:{" "}
                            {enumItem.idEnum})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              }

              return (
                <TextField
                  fullWidth
                  label={t("nodeComponents.configuration.value") || "Value"}
                  value={formData.value}
                  onChange={(e) => handleFormChange("value", e.target.value)}
                  variant="outlined"
                  helperText={(() => {
                    if (selectedObject) {
                      switch (selectedObject.type) {
                        case 0:
                          return t(
                            "nodeComponents.configuration.valueTypes.boolean"
                          );
                        case 2:
                          return t(
                            "nodeComponents.configuration.valueTypes.integer"
                          );
                        case 5:
                          return t(
                            "nodeComponents.configuration.valueTypes.decimal"
                          );
                        case 10:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied1000"
                          );
                        case 11:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied100"
                          );
                        case 12:
                          return t(
                            "nodeComponents.configuration.valueTypes.multiplied10"
                          );
                        default:
                          return t(
                            "nodeComponents.configuration.valueTypes.text"
                          );
                      }
                    }
                    return "";
                  })()}
                />
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDialogClose}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {editingConfig
              ? t("common.update") || "Update"
              : t("common.add") || "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          {t("nodeComponents.configuration.deleteConfirmTitle") ||
            "Delete Configuration"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, textAlign: "center" }}>
          <Typography>
            {t("nodeComponents.configuration.deleteConfirmMessage") ||
              "Are you sure you want to delete this configuration?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "center" }}>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {t("nodeComponents.configuration.delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
